//@flow

import React, { CSSProperties, useRef } from "react";
import classNames from "classnames";

import "./style.scss";

export type Props =
  | CSSProperties
  | {
      [string]: any,
      ref?: string,
      id?: string,
      name?: string,
      title?: string,
      html?: string,
      color?: string,
      className?: string | string[],
      onClick?: Function,
      onMouseEnter?: Function,
      onMouseLeave?: Function,
      onMouseOver?: Function,
      onMouseOut?: Function,
      onMouseUp?: Function,
      onMouseDown?: Function,
      draggable?: boolean,
      onDragStart?: Function,
      onDrag?: Function,
      onDragOver?: Function,
      onDragEnd?: Function,
      display?: string,
      disabled?: boolean,
      children: React$Node[],
      cursor?:
        | "alias"
        | "all-scroll"
        | "auto"
        | "cell"
        | "col-resize"
        | "context-menu"
        | "copy"
        | "crosshair"
        | "default"
        | "e-resize"
        | "ew-resize"
        | "grab"
        | "grabbing"
        | "help"
        | "move"
        | "n-resize"
        | "ne-resize"
        | "nesw-resize"
        | "ns-resize"
        | "nw-resize"
        | "nwse-resize"
        | "no-drop"
        | "none"
        | "not-allowed"
        | "pointer"
        | "progress"
        | "row-resize"
        | "s-resize"
        | "se-resize"
        | "sw-resize"
        | "text"
        | "url"
        | "w-resize"
        | "wait"
        | "zoom-in"
        | "zoom-out",
      debug?: boolean,
      gap?: number,
      backgroundColor?: string,
      backgroundImage?: string | "none",
      backgroundPosition?: "bottom" | "center" | "left" | "right" | "top",
      backgroundSize?: "auto" | "contain" | "cover",
      flex?: number,
      flexGrow?: number,
      width?: string | number,
      minWidth?: string | number,
      maxWidth?: string | number,
      height?: string | number,
      minHeight?: string | number,
      maxHeight?: string | number,
      separator?: "horizontal" | "vertical",
      opacity?: number,
      separatorColor?: string,
      outline?: string,
      alignItems?:
        | "normal"
        | "stretch"
        | "center"
        | "start"
        | "end"
        | "flex-start"
        | "flex-end"
        | "self-start"
        | "self-end"
        | "anchor-center"
        | "baseline"
        | "first baseline"
        | "last baseline"
        | "safe center"
        | "unsafe center"
        | "inherit"
        | "initial"
        | "revert"
        | "revert-layer"
        | "unset",
      alignSelf:
        | "flex-start"
        | "flex-end"
        | "center"
        | "space-between"
        | "space-around"
        | "space-evenly"
        | "start"
        | "end"
        | "left"
        | "right",
      direction?: "row" | "column",
      flexWrap?: "nowrap" | "wrap" | "wrap-reverse",
      position?: "absolute" | "relative",
      padding?: string | number | number[] | string[],
      margin?: string | number | number[] | string[],
      filter?: string,
      overflow:
        | "auto"
        | "hidden"
        | "overlay"
        | "scroll"
        | "visible"
        | "inherit"
        | "initial"
        | "revert"
        | "revert-layer"
        | "unset",
      justifyContent?:
        | "flex-start"
        | "flex-end"
        | "center"
        | "space-between"
        | "space-around"
        | "space-evenly"
        | "start"
        | "end"
        | "left"
        | "right",
      border?: CSSProperties.border,
      borderColor?: CSSProperties.borderColor,
      borderRadius?: string | number,
      shadow?: CSSProperties.boxShadow,
      gridTemplateColumns?: number,
      rowStart?: number,
      colStart?: number,
      rowEnd?: number,
      colEnd?: number,
      top?: number,
      right?: number,
      bottom?: number,
      left?: number,
      zIndex?: number,
    };

export const View = React.forwardRef((props, ref) => {
  const {
    id,
    name,
    title,
    html,
    color,
    cursor,
    display,
    disabled,
    className,
    shadow,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onMouseOut,
    onMouseUp,
    onMouseDown,
    draggable,
    onDragStart,
    onDrag,
    onDragOver,
    onDragEnd,
    debug,
    children,
    gap,
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundSize,
    flexGrow,
    width,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    height,
    border,
    borderColor,
    borderRadius,
    flex,
    overflow,
    direction,
    flexWrap,
    justifyContent,
    alignItems,
    alignSelf,
    filter,
    position,
    padding,
    margin,
    separator,
    opacity,
    separatorColor,
    outline,
    gridTemplateColumns,
    rowStart,
    colStart,
    rowEnd,
    colEnd,
    top,
    right,
    bottom,
    left,
    zIndex,
    ...restProps
  } = props;
  //$FlowIgnore
  function parsePaddingOrMargin(val: number | number[] | string | string[] | []) {
    //$FlowIgnore
    if (Array.isArray(val) && val.length === 2) val.push(...val);

    if (!val) return "";
    let res;
    if (typeof val === "string") {
      return val;
    } else if (typeof val === "number") {
      res = `${val * 4}${"px"}`;
    } else if (val.map) {
      res = val
        .map(v => {
          if (typeof v == "number") return `${v * 4}px`;
          else return v;
        })
        .join(" ");
    }
    return res;
  }

  function parsePixelValue(val: number | string | null) {
    if (typeof val !== "string" && typeof val !== "number") return null;
    if (typeof val === "string") return val;
    if (typeof val === "number") return `${val * 4}px`;
  }

  const viewStyle = {
    id: id || null,
    name: name || null,

    color: color || null,
    cursor: cursor,
    backgroundColor: "",
    backgroundImage: backgroundImage && `url("${backgroundImage}")`,
    backgroundPosition: backgroundPosition || null,
    backgroundSize: backgroundSize ? `${backgroundSize}` : null,
    boxShadow: shadow,
    display: display || null,
    pointerEvents: "all",
    gap: gap != undefined ? `${gap * 4}px` : null,
    flexGrow: flexGrow || 0,
    width: width && parsePixelValue(width),
    height: height && parsePixelValue(height),
    minWidth: minWidth && parsePixelValue(minWidth),
    maxWidth: maxWidth && parsePixelValue(maxWidth),
    minHeight: minHeight && parsePixelValue(minHeight),
    maxHeight: maxHeight && parsePixelValue(maxHeight),
    flex: flex || null,
    flexWrap: flexWrap || null,
    overflow: overflow || null,
    flexDirection: direction || "column",
    justifyContent: justifyContent || "flex-start",
    alignItems: alignItems || null,
    alignSelf: alignSelf,
    filter: filter || null,
    position: position || null,
    padding: padding && parsePaddingOrMargin(padding),
    margin: margin && parsePaddingOrMargin(margin),
    outline: outline || null,
    opacity: opacity,
    border: border,
    borderColor: borderColor,
    borderRadius:
      typeof borderRadius === "number"
        ? `${borderRadius}rem`
        : borderRadius || (border && "0.4rem") || "",
    gridTemplateColumns: gridTemplateColumns ? `repeat(${gridTemplateColumns}, 1fr)` : null,
    gridRowStart: rowStart || null,
    gridColumnStart: colStart || null,
    gridRowEnd: rowEnd || null,
    gridColumnEnd: colEnd || null,
    top: top && parsePixelValue(top),
    right: right && parsePixelValue(right),
    bottom: bottom && parsePixelValue(bottom),
    left: left && parsePixelValue(left),
    zIndex: zIndex,
  };

  // Print stylesheets need to tell a column View from a row one and to know its
  // gap, because on paper the vertical chain has to leave flex layout (Safari
  // only honours page breaks under a block parent) and gap does not survive it.
  let viewClassName = ["view", `view-${direction || "column"}`];
  if (gap != undefined) {
    viewClassName.push(`view-gap-${gap}`);
  }
  if (className) {
    if (typeof className === "string") {
      viewClassName = [...viewClassName, ...className.split(" ")];
    }

    if (typeof className === "object") {
      viewClassName = [...viewClassName, ...className.filter(cls => typeof cls === "string")];
    }
  }

  if (color) {
    if (color.includes("var(")) {
      viewStyle.color = color;
    } else {
      viewClassName.push(`text-color-${color}`);
    }
  }

  if (backgroundColor) {
    if (backgroundColor.includes("var(")) {
      viewStyle.backgroundColor = backgroundColor;
    } else {
      viewClassName.push(`background-color-${backgroundColor}`);
    }
  }
  if (separator) {
    if (typeof separator === "string") {
      viewStyle[
        `border${separator.charAt(0).toUpperCase() + separator.slice(1)}`
      ] = `1px solid ${separatorColor || "var(--default-separator-color)"}`;
    }
  }

  if (!!disabled) {
    viewStyle.filter = "opacity(0.5)";
    viewStyle.pointerEvents = "none";
  }

  if (debug) {
    viewStyle.border = "1px dashed #ff9f9f";
  }

  return props.html ? (
    <div
      {...restProps}
      id={id}
      ref={ref}
      data-name={name}
      title={title}
      style={viewStyle}
      className={viewClassName.join(" ")}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseUp={onMouseUp}
      onMouseDown={onMouseDown}
      draggable={draggable}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      dangerouslySetInnerHTML={{ __html: props.html }}
    />
  ) : (
    <div
      {...restProps}
      id={id}
      ref={ref}
      data-name={name}
      title={title}
      style={viewStyle}
      className={viewClassName.join(" ")}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseUp={onMouseUp}
      onMouseDown={onMouseDown}
      draggable={draggable}
      onDragStart={onDragStart}
      onDrag={onDrag}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      {children}
    </div>
  );
});
